import { ref } from 'vue'

import { type QueueIncidentErrorConfig } from '@collector/desktop-feature-incidents-queue-core'
import {
  CommonIncidentId,
  EventStatusId,
  IncidentId,
} from '@collector/sportsapi-client-legacy'

import {
  Clock,
  provideAddIncidentConfig,
  provideAssignIncidentOnPitchConfig,
  provideAssignPlayerOnPitchConfig,
  provideAttributesToResetParticipants,
  provideBetIncidents,
  provideClock,
  provideCustomIncidentNames,
  provideDefaultSkinPatternId,
  provideEventInfoConfig,
  provideEventRelatedStatusConfig,
  provideEventStatusAcronymConfig,
  provideEventStatuses,
  provideEventStatusIncidentsOnFieldConfig,
  provideFieldClickCoordinatesIncidentConfig,
  provideHandyBarInfoAlwaysVisibleIncidents,
  provideHasChangeSides,
  provideHistoryFiltersConfig,
  provideHistoryIncidentsConfig,
  provideImportantStatsId,
  provideIncidentIdsGroup,
  provideIncidentsCustomFiltersConfig,
  provideIncidentsQueuePayloadConfig,
  provideIncidentsRequireConfirmation,
  provideIncidentsRequirePointingPositionConfig,
  provideIncidentsResultConfig,
  provideIncidentsTeamPossessionConfig,
  provideLineupConfig,
  provideManualAddIncidentConfig,
  providePitchBlockedEventStatus,
  providePitchIncidentsConfig,
  providePitchOverlaySupportedStatuses,
  providePitchPenaltyOverlayConfig,
  providePlayersConfig,
  providePocketIncidentsConfig,
  provideQueueIncidentErrorConfig,
  provideResultWithSliderConfig,
  provideSameTimeIncidentsConfig,
  provideVerifyResultSummaryConfig,
  provideVIPIncidentsConfig,
} from '.'
import {
  AddIncidentConfig,
  AssignIncidentOnPitchConfig,
  AssignPlayerOnPitchConfig,
  AttributesToResetParticipants,
  BetIncidents,
  EventInfoControls,
  EventRelatedStatusConfig,
  EventStatusAcronymConfig,
  EventStatusIncidentsOnFieldConfig,
  FieldClickCoordinatesIncidentConfig,
  HandyBarInfoAlwaysVisibleIncidents,
  HistoryFiltersConfig,
  HistoryIncidentsConfig,
  ImportantStats,
  IncidentIdsGroup,
  IncidentsCustomFiltersConfig,
  IncidentsQueuePayloadConfig,
  IncidentsRequirePointingPositionConfig,
  IncidentsResultConfig,
  IncidentsTeamPossessionConfig,
  LastIncidentsConfig,
  LineupConfig,
  ManualAddIncidentConfig,
  PitchBlockedEventStatus,
  PitchIncidentsConfig,
  PitchOverlaySupportedStatuses,
  PitchPenaltyOverlayConfig,
  PlayersConfig,
  PocketIncidentsConfig,
  ResultWithSliderConfig,
  SameTimeIncidentsConfig,
  VerifyResultSummaryConfig,
  VIPIncidentsConfig,
} from '../configuration'
import { provideLastIncidentsConfig } from './lastIncidentsConfig'

type Params = {
  addIncidentConfig?: AddIncidentConfig
  attributesToResetParticipants?: AttributesToResetParticipants
  betIncidents?: BetIncidents
  clock?: Clock
  customIncidentNames?: Map<IncidentId, string>
  defaultSkinPatternId?: number
  eventInfoControls?: EventInfoControls
  eventRelatedStatusConfig?: EventRelatedStatusConfig
  eventStatusAcronymConfig?: EventStatusAcronymConfig
  eventStatuses?: EventStatusId[]
  hasChangeSides?: boolean
  handyBarInfoAlwaysVisibleIncidents?: HandyBarInfoAlwaysVisibleIncidents
  historyFiltersConfig?: HistoryFiltersConfig
  historyIncidentsConfig?: HistoryIncidentsConfig
  importantStatsId?: ImportantStats
  incidentIdsGroup?: IncidentIdsGroup
  incidentsCustomFiltersConfig?: IncidentsCustomFiltersConfig
  incidentsQueuePayloadConfig?: IncidentsQueuePayloadConfig
  incidentsRequireConfirmation?: IncidentId[]
  incidentsResultConfig?: IncidentsResultConfig
  incidentsTeamPossessionConfig?: IncidentsTeamPossessionConfig
  lastIncidentsConfig?: LastIncidentsConfig
  lineupConfig?: LineupConfig
  manualAddIncidentConfig?: ManualAddIncidentConfig
  pitch?: {
    assignIncidentOnPitchConfig?: AssignIncidentOnPitchConfig
    assignPlayerOnPitchConfig?: AssignPlayerOnPitchConfig
    eventStatusIncidentsOnFieldConfig?: EventStatusIncidentsOnFieldConfig
    fieldClickCoordinatesIncidentConfig?: FieldClickCoordinatesIncidentConfig
    incidentsRequirePointingPositionConfig?: IncidentsRequirePointingPositionConfig
    pitchBlockedEventStatus?: PitchBlockedEventStatus
    pitchIncidentsConfig?: PitchIncidentsConfig
    pitchOverlaySupportedStatuses?: PitchOverlaySupportedStatuses
    pitchPenaltyOverlayConfig?: PitchPenaltyOverlayConfig
  }
  playersConfig?: PlayersConfig
  pocketIncidentsConfig?: PocketIncidentsConfig
  resultWithSliderConfig?: ResultWithSliderConfig
  sameTimeIncidentsConfig?: SameTimeIncidentsConfig
  queueIncidentErrorConfig?: QueueIncidentErrorConfig
  verifyResultSummaryConfig?: VerifyResultSummaryConfig
  vipIncidentsConfig?: VIPIncidentsConfig
}

/**
 * Provide custom configuration for a sport, which will render relation view components with the passed data.
 *
 * Each property of the configuration might be:
 *
 * - an empty object matching the required type (`{}`, `[]`, `Set`, `Map`, etc.): components are rendered, but without data
 *
 * - custom data of the required type: components are rendered with the custom data
 *
 * - `null`: the property will be resolved to its default value
 *
 * - `undefined`: the property is optional in the sport
 *
 */
// The function cannot be async – Vue will show a warning and won't resolve the value provided with inject.
export function initSportConfiguration(params: Params = {}): void {
  if (params.addIncidentConfig) {
    provideAddIncidentConfig(params.addIncidentConfig)
  }

  if (params.pitch?.assignPlayerOnPitchConfig) {
    provideAssignPlayerOnPitchConfig(params.pitch.assignPlayerOnPitchConfig)
  }

  if (params.pitch?.assignIncidentOnPitchConfig) {
    provideAssignIncidentOnPitchConfig(params.pitch.assignIncidentOnPitchConfig)
  }

  if (params.attributesToResetParticipants) {
    provideAttributesToResetParticipants(params.attributesToResetParticipants)
  }

  provideBetIncidents(
    new Set([
      CommonIncidentId.Betstart,
      CommonIncidentId.Betstop,
      ...(params.betIncidents || []),
    ]),
  )

  if (params.hasChangeSides !== undefined) {
    provideHasChangeSides(params.hasChangeSides)
  }

  if (params.clock) {
    provideClock(params.clock)
  }

  if (params.customIncidentNames) {
    provideCustomIncidentNames(params.customIncidentNames)
  }

  if (params.defaultSkinPatternId) {
    provideDefaultSkinPatternId(params.defaultSkinPatternId)
  }

  if (params.eventInfoControls) {
    provideEventInfoConfig({
      eventInfoControls: params.eventInfoControls,
      isEventInfoPopoverOpen: ref(false),
    })
  }

  if (params.eventRelatedStatusConfig) {
    provideEventRelatedStatusConfig(params.eventRelatedStatusConfig)
  }

  if (params.eventStatusAcronymConfig) {
    provideEventStatusAcronymConfig(params.eventStatusAcronymConfig)
  }

  if (params.eventStatuses) {
    provideEventStatuses(params.eventStatuses)
  }

  if (params.pitch?.eventStatusIncidentsOnFieldConfig) {
    provideEventStatusIncidentsOnFieldConfig(
      params.pitch.eventStatusIncidentsOnFieldConfig,
    )
  }

  if (params.pitch?.fieldClickCoordinatesIncidentConfig) {
    provideFieldClickCoordinatesIncidentConfig(
      params.pitch.fieldClickCoordinatesIncidentConfig,
    )
  }

  if (params.handyBarInfoAlwaysVisibleIncidents) {
    provideHandyBarInfoAlwaysVisibleIncidents(
      params.handyBarInfoAlwaysVisibleIncidents,
    )
  }

  if (params.historyFiltersConfig) {
    provideHistoryFiltersConfig(params.historyFiltersConfig)
  }

  if (params.historyIncidentsConfig) {
    provideHistoryIncidentsConfig(params.historyIncidentsConfig)
  }

  if (params.importantStatsId) {
    provideImportantStatsId(params.importantStatsId)
  }

  if (params.incidentIdsGroup) {
    provideIncidentIdsGroup(params.incidentIdsGroup)
  }

  if (params.incidentsCustomFiltersConfig) {
    provideIncidentsCustomFiltersConfig(params.incidentsCustomFiltersConfig)
  }

  if (params.incidentsQueuePayloadConfig) {
    provideIncidentsQueuePayloadConfig(params.incidentsQueuePayloadConfig)
  }

  if (params.incidentsResultConfig) {
    provideIncidentsResultConfig(params.incidentsResultConfig)
  }

  if (params.pitch?.incidentsRequirePointingPositionConfig) {
    provideIncidentsRequirePointingPositionConfig(
      params.pitch.incidentsRequirePointingPositionConfig,
    )
  }

  if (params.incidentsTeamPossessionConfig) {
    provideIncidentsTeamPossessionConfig(params.incidentsTeamPossessionConfig)
  }

  if (params.incidentsRequireConfirmation) {
    provideIncidentsRequireConfirmation(params.incidentsRequireConfirmation)
  }

  if (params.lastIncidentsConfig) {
    provideLastIncidentsConfig(params.lastIncidentsConfig)
  }

  if (params.lineupConfig) {
    provideLineupConfig(params.lineupConfig)
  }

  if (params.manualAddIncidentConfig) {
    provideManualAddIncidentConfig(params.manualAddIncidentConfig)
  }

  providePitchBlockedEventStatus(
    params.pitch?.pitchBlockedEventStatus || new Set(),
  )

  if (params.pitch?.pitchIncidentsConfig) {
    providePitchIncidentsConfig(params.pitch.pitchIncidentsConfig)
  }

  if (params.pitch?.pitchOverlaySupportedStatuses) {
    providePitchOverlaySupportedStatuses(
      params.pitch.pitchOverlaySupportedStatuses,
    )
  }

  if (params.pitch?.pitchPenaltyOverlayConfig) {
    providePitchPenaltyOverlayConfig(params.pitch.pitchPenaltyOverlayConfig)
  }

  if (params.playersConfig) {
    providePlayersConfig(params.playersConfig)
  }

  if (params.pocketIncidentsConfig) {
    providePocketIncidentsConfig(params.pocketIncidentsConfig)
  }

  if (params.resultWithSliderConfig) {
    provideResultWithSliderConfig(params.resultWithSliderConfig)
  }

  if (params.sameTimeIncidentsConfig) {
    provideSameTimeIncidentsConfig(params.sameTimeIncidentsConfig)
  }

  if (params.queueIncidentErrorConfig) {
    provideQueueIncidentErrorConfig(params.queueIncidentErrorConfig)
  }

  if (params.verifyResultSummaryConfig) {
    provideVerifyResultSummaryConfig(params.verifyResultSummaryConfig)
  }

  if (params.vipIncidentsConfig) {
    provideVIPIncidentsConfig(params.vipIncidentsConfig)
  }
}
